import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  X,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Order, OrderStatus } from '../types';
import { formatCurrency, formatDateTime } from '../lib/utils';

interface UpdateStatusModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate?: (orderId: string, newStatus: OrderStatus, notes?: string) => Promise<void>;
}

interface StatusOption {
  value: OrderStatus;
  label: string;
  description: string;
  color: string;
  icon: any;
}

const statusOptions: StatusOption[] = [
  {
    value: 'pending',
    label: 'Pending',
    description: 'Order has been placed and is awaiting processing',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
  },
  {
    value: 'paid',
    label: 'Paid',
    description: 'Payment confirmed, ready for fulfillment',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Package,
  },
  {
    value: 'processed',
    label: 'Processed',
    description: 'Order has been processed and is ready for shipment',
    color: 'bg-blue-100 text-blue-900 border-blue-200',
    icon: Package,
  },
  {
    value: 'shipped',
    label: 'Shipped',
    description: 'Order has been shipped and is on its way to the customer',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Truck,
  },
  {
    value: 'delivered',
    label: 'Delivered',
    description: 'Order has been successfully delivered to the customer',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    description: 'Order has been cancelled',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: XCircle,
  },
];

export function UpdateStatusModal({ order, isOpen, onClose, onStatusUpdate }: UpdateStatusModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(null);
  const [notes, setNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const currentStatusOption = statusOptions.find(option => option.value === order?.status);
  const selectedStatusOption = statusOptions.find(option => option.value === selectedStatus);
  const isStatusChanged = selectedStatus && selectedStatus !== order?.status;

  const handleStatusUpdate = async () => {
    if (!order || !selectedStatus) {
      console.error('Order or selected status is missing');
      toast.error('Please select a status');
      return;
    }

    if (!onStatusUpdate) {
      console.error('onStatusUpdate function not provided');
      toast.error('Status update function not available');
      return;
    }

    try {
      setIsUpdating(true);
      console.log('Calling onStatusUpdate with:', {
        orderId: order.id,
        newStatus: selectedStatus,
        notes: notes.trim() || undefined
      });
      
      await onStatusUpdate(order.id, selectedStatus, notes.trim() || undefined);
      
      toast.success('Order status updated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getOrderNumber = () => {
    if (!order) return '';
    return order.orderNumber || order.id;
  };

  if (!order) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      Update Order Status
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      <span>Order: {getOrderNumber()}</span>
                      <span>Customer: {order.customer.name}</span>
                      <span>Total: {formatCurrency(order.total)}</span>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Current Status */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Current Status</h3>
                  <div className="flex items-center gap-3 mb-3">
                    {currentStatusOption && (
                      <>
                        <Badge className={`${currentStatusOption.color} border rounded-lg px-3 py-1.5`}>
                          <currentStatusOption.icon className="w-3 h-3 mr-1" />
                          {currentStatusOption.label}
                        </Badge>
                        <span className="text-sm text-gray-600">{currentStatusOption.description}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Last updated: {formatDateTime(order.updatedAt)}
                  </div>
                </motion.div>

                {/* Status Selection */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3"
                >
                  <h3 className="text-sm font-medium text-gray-900">Select New Status</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {statusOptions.map((option, index) => {
                      const isSelected = selectedStatus === option.value;
                      const isCurrent = order.status === option.value;
                      
                      return (
                        <motion.button
                          key={option.value}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedStatus(option.value)}
                          disabled={isCurrent}
                          className={`
                            p-4 rounded-xl border-2 text-left transition-all duration-300
                            ${isSelected && !isCurrent
                              ? 'border-indigo-500 bg-indigo-50 shadow-lg shadow-indigo-500/20'
                              : isCurrent
                              ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={`${option.color} border rounded-lg`}>
                              <option.icon className="w-3 h-3 mr-1" />
                              {option.label}
                            </Badge>
                            {isCurrent && (
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">(Current)</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{option.description}</p>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Status Change Summary */}
                {isStatusChanged && selectedStatusOption && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-indigo-50 border border-indigo-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-indigo-600" />
                      <h3 className="text-sm font-medium text-indigo-900">Status Change Preview</h3>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge className={`${currentStatusOption?.color} border rounded-lg`}>
                        {currentStatusOption?.label}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-indigo-600" />
                      <Badge className={`${selectedStatusOption.color} border rounded-lg`}>
                        {selectedStatusOption.label}
                      </Badge>
                    </div>
                  </motion.div>
                )}

                {/* Notes */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="space-y-3"
                >
                  <Label htmlFor="status-notes" className="text-sm font-medium text-gray-900">
                    Notes (Optional)
                  </Label>
                  <Textarea
                    id="status-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this status update..."
                    className="min-h-[80px] resize-none rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                    rows={3}
                  />
                </motion.div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={isUpdating}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={handleStatusUpdate}
                    disabled={!isStatusChanged || isUpdating}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-indigo-600/25"
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Package className="w-4 h-4 mr-2" />
                        Update Status
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
} 